/**
 * useDirectorAccess — reactive version of isDirector() (src/utils/access.ts).
 *
 * isDirector() reads the localStorage ownerIds cache synchronously. That's
 * correct once the tenant has loaded, but on a cold start (fresh login, new
 * device, PWA relaunch) the cache is empty until TenantContext's Firestore
 * listener delivers the tenant doc — a component that checks once at render
 * locks a REAL director into "Access Restricted" until a manual refresh.
 *
 * This hook resolves access from the LIVE tenant via useTenant() (so it
 * re-renders when the tenant lands), falls back to the cached last-known-good
 * list while the tenant isn't loaded (cold start, or the neutral fallback
 * after a terminal listener error — see cacheOwnerIds), and reports
 * `checking` while the answer isn't authoritative yet so gates can show a
 * spinner instead of a false denial.
 */
import { useTenant } from '@/hooks/useTenant';
import { getOwnerIdsSync } from '@/utils/access';
import { UNCONFIGURED_TENANT_ID } from '@/types/tenant.types';

export interface DirectorAccess {
  /** True when the logged-in user_id is in tenant.ownerIds (live or cached). */
  isDirector: boolean;
  /** tenant.ownerIds as resolved above — the autobuild thread marks these people's posts as instructions. */
  ownerIds: string[];
  /**
   * True while the tenant is still loading AND the cache doesn't already
   * grant access — i.e. "don't deny yet, show a spinner". Never true once
   * access is granted (a cached grant renders immediately, no spinner flash).
   */
  checking: boolean;
}

export function useDirectorAccess(): DirectorAccess {
  const { tenant, isLoading } = useTenant();

  const userId = (localStorage.getItem('user_id') || '').toLowerCase();
  const tenantLoaded = !!tenant && tenant.id !== UNCONFIGURED_TENANT_ID;
  // Live ownerIds once a real tenant is loaded; cached last-known-good
  // otherwise (cold start, or the neutral fallback config after an error).
  const ownerIds = (tenantLoaded
    ? (Array.isArray(tenant.ownerIds) ? tenant.ownerIds : [])
    : getOwnerIdsSync()
  ).filter((id): id is string => typeof id === 'string');
  if (!userId) return { isDirector: false, ownerIds, checking: false };

  const isDirector = ownerIds.some((id) => id.toLowerCase() === userId);

  return { isDirector, ownerIds, checking: !tenantLoaded && isLoading && !isDirector };
}
